import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* ── Open-dialog stack ──────────────────────────────────────────────────────
   Every open Modal registers here. Only the top-most one reacts to Escape and
   traps Tab, so a confirm opened over a form closes on its own.

   Order is by OPEN sequence (a number taken at first render), not by when the
   layout effect runs: when a parent and a child Modal mount in the same commit
   the child's effect runs first, but the child rendered later, so it is still
   the top-most. Each Modal's z-index follows its position in the stack.

   Layering (workspace): drawer 340–350 · popovers 500–600 · task panel 1200–1201 ·
   Modal 1300 + depth×10 · export menu 1600 · MoreMenu 2000 · guided tour 3000 ·
   toasts 9999 (react-toastify). Anything a dialog can open must sit above 1300. */
export const MODAL_Z_BASE = 1300;
const stack = [];          // [{ seq, notify }], ascending seq
const isTop = (token) => stack.length > 0 && stack[stack.length - 1] === token;
let openSeq = 0;
const notifyAll = () => stack.forEach((t, i) => t.notify(i));

const FOCUSABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', 'audio[controls]', 'video[controls]',
  '[contenteditable]:not([contenteditable="false"])', '[tabindex]:not([tabindex="-1"])',
].join(',');
const FIELDS = 'input:not([disabled]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]), select:not([disabled]), textarea:not([disabled])';

/* jsdom (tests) has no layout, so treat everything as visible there. */
const NO_LAYOUT = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '');
const visible = (el) => NO_LAYOUT || !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
export const focusablesIn = (root) => (root ? Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => visible(el) && !el.closest('[inert]')) : []);

/** Portal target: the workspace root (so --cw-* tokens and .cw-root rules apply), else <body>. */
const portalTarget = () => (typeof document === 'undefined' ? null : (document.querySelector('.cw-root') || document.body));

const ModalContext = createContext({ requestClose: () => {}, dirty: false });

/**
 * Inside a Modal: `const close = useModalClose();` — use for footer Cancel
 * buttons so they get the same "Discard changes?" guard as Escape and ✕.
 */
export const useModalClose = () => useContext(ModalContext).requestClose;

/**
 * <Modal /> — the workspace dialog shell.
 *
 * @param {ReactNode} title          heading; labels the dialog (aria-labelledby)
 * @param {ReactNode} [description]  optional sub-heading; describes the dialog (aria-describedby)
 * @param {ReactNode|function} [footer]  action row. May be `({ requestClose }) => …` so a
 *                                   Cancel button can go through the dirty guard.
 * @param {()=>void} onClose         called when the dialog should close
 * @param {boolean} [dirty=false]    unsaved changes: backdrop click is ignored, and
 *                                   Escape / ✕ / requestClose ask "Discard changes?" first
 * @param {string|number} [maxWidth=560]
 * @param {'dialog'|'alertdialog'} [role='dialog']
 * @param {React.RefObject|string} [initialFocus]  element (ref or selector) to focus on open.
 *                                   Default: an element already focused via autoFocus, else
 *                                   the first form field, else the first focusable control.
 * @param {boolean} [closeOnBackdrop=true]  set false for dialogs that must be answered
 * @param {boolean} [hideClose=false]  hide the ✕ button
 *
 * Focus is trapped inside while open and returned to the opener on close.
 */
const Modal = ({
  title, description, children, footer, onClose, maxWidth = 560,
  dirty = false, role = 'dialog', initialFocus, closeOnBackdrop = true, hideClose = false,
  className = '', bodyClassName = '', bodyStyle,
}) => {
  const uid = useId().replace(/:/g, '');
  const titleId = `cw-dlg-${uid}-t`;
  const descId = `cw-dlg-${uid}-d`;
  const dialogRef = useRef(null);
  const [seq] = useState(() => { openSeq += 1; return openSeq; });
  const tokenRef = useRef(null);
  if (!tokenRef.current) tokenRef.current = { seq, notify: () => {} };
  const [depth, setDepth] = useState(() => stack.length);
  const [confirming, setConfirming] = useState(false);
  const pressStartedOnBackdrop = useRef(false);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const requestClose = useCallback(() => {
    if (dirtyRef.current) { setConfirming(true); return; }
    if (onCloseRef.current) onCloseRef.current();
  }, []);

  // Register on the stack, remember the opener, restore focus on close.
  useLayoutEffect(() => {
    const token = tokenRef.current;
    const opener = typeof document !== 'undefined' ? document.activeElement : null;
    token.notify = (i) => setDepth(i);
    const at = stack.findIndex((t) => t.seq > token.seq);
    if (at === -1) stack.push(token); else stack.splice(at, 0, token);
    notifyAll();
    return () => {
      const i = stack.lastIndexOf(token);
      if (i !== -1) stack.splice(i, 1);
      token.notify = () => {};
      notifyAll();
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        try { opener.focus({ preventScroll: true }); } catch { opener.focus(); }
      }
    };
  }, []);

  // Initial focus.
  useEffect(() => {
    const root = dialogRef.current;
    if (!root) return;
    if (root.contains(document.activeElement) && document.activeElement !== root) return; // autoFocus child
    let target = null;
    if (initialFocus) target = typeof initialFocus === 'string' ? root.querySelector(initialFocus) : initialFocus.current;
    if (!target) target = Array.from(root.querySelectorAll(FIELDS)).find(visible);
    if (!target) target = focusablesIn(root).find((el) => !el.classList.contains('cw-modal-x'));
    (target || root).focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape (top-most only) + Tab trap.
  useEffect(() => {
    const onKey = (e) => {
      if (!isTop(tokenRef.current)) return;
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return;
        e.preventDefault();
        requestClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const items = focusablesIn(root);
      if (items.length === 0) { e.preventDefault(); root.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!root.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && (active === first || active === root)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  const onBackdropDown = (e) => { pressStartedOnBackdrop.current = e.target === e.currentTarget; };
  const onBackdropClick = (e) => {
    e.stopPropagation();
    // Only a press that started AND ended on the backdrop closes — dragging a text
    // selection out of an input must not dismiss the form.
    const started = pressStartedOnBackdrop.current;
    pressStartedOnBackdrop.current = false;
    if (e.target !== e.currentTarget || !started) return;
    if (!closeOnBackdrop || dirtyRef.current) return;
    requestClose();
  };

  const footerNode = typeof footer === 'function' ? footer({ requestClose }) : footer;

  const node = (
    <ModalContext.Provider value={{ requestClose, dirty }}>
      <div
        className="cw-modal-backdrop"
        style={{ zIndex: MODAL_Z_BASE + depth * 10 }}
        onMouseDown={onBackdropDown}
        onClick={onBackdropClick}
      >
        <div
          ref={dialogRef}
          className={`cw-card cw-modal ${className}`.trim()}
          role={role}
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={description ? descId : undefined}
          tabIndex={-1}
          style={{ maxWidth }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="cw-modal-head">
            <div style={{ minWidth: 0 }}>
              <h2 id={titleId} className="cw-modal-title">{title}</h2>
              {description && <p id={descId} className="cw-modal-desc">{description}</p>}
            </div>
            {!hideClose && (
              <button type="button" className="cw-modal-x" onClick={requestClose} aria-label="Close">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            )}
          </div>
          {React.Children.count(children) > 0 && (
            <div className={`cw-modal-body cw-scroll ${bodyClassName}`.trim()} style={bodyStyle}>{children}</div>
          )}
          {footerNode && <div className="cw-modal-foot">{footerNode}</div>}
        </div>
      </div>
      {confirming && (
        <Modal
          role="alertdialog"
          title="Discard changes?"
          description="You have unsaved changes. If you close now, they'll be lost."
          maxWidth={420}
          closeOnBackdrop={false}
          onClose={() => setConfirming(false)}
          initialFocus=".cw-btn-secondary"
          footer={(
            <>
              <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setConfirming(false)}>Keep editing</button>
              <button
                type="button"
                className="cw-btn cw-btn-danger"
                onClick={() => { setConfirming(false); if (onCloseRef.current) onCloseRef.current(); }}
              >
                Discard changes
              </button>
            </>
          )}
        />
      )}
    </ModalContext.Provider>
  );

  const target = portalTarget();
  return target ? createPortal(node, target) : node;
};

/* ── Field ─────────────────────────────────────────────────────────────────── */

const NATIVE = new Set(['input', 'select', 'textarea']);
const NO_INPUT_CLASS = new Set(['checkbox', 'radio', 'file', 'range', 'color']);
const isNativeControl = (el) => React.isValidElement(el) && typeof el.type === 'string' && NATIVE.has(el.type);

/**
 * <Field label hint error required> <input …/> </Field>
 *
 * Renders a real <label htmlFor>. The first native <input>/<select>/<textarea>
 * child gets an auto-generated id (unless it has one), the `cw-input` class
 * (unless it has a className), aria-invalid + aria-describedby for error/hint,
 * and aria-required when `required`. For a custom control (Combobox, …) the id
 * is passed through as a prop and the wrapper is a labelled group.
 *
 * @param {ReactNode} label
 * @param {boolean}   [required]   shows a * and sets aria-required (no native validation)
 * @param {ReactNode} [error]      inline error message; marks the control invalid
 * @param {ReactNode} [hint]       helper text under the control
 * @param {string}    [id]         control id to use instead of the generated one
 */
export const Field = ({ label, children, required, error, hint, id, className = '', style }) => {
  const uid = useId().replace(/:/g, '');
  const labelId = `cw-fl-${uid}`;
  const errId = error ? `cw-fe-${uid}` : undefined;
  const hintId = hint ? `cw-fh-${uid}` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(' ') || undefined;

  const all = React.Children.toArray(children);
  let nativeIndex = all.findIndex(isNativeControl);
  const onlyCustom = nativeIndex === -1 && all.length === 1 && React.isValidElement(all[0]) && typeof all[0].type !== 'string';
  if (onlyCustom) nativeIndex = 0;
  const target = nativeIndex >= 0 ? all[nativeIndex] : null;
  const controlId = id || target?.props?.id || `cw-fc-${uid}`;

  let content = children;
  if (target) {
    const p = target.props || {};
    const extra = { id: controlId };
    if (describedBy) extra['aria-describedby'] = [p['aria-describedby'], describedBy].filter(Boolean).join(' ');
    if (error) extra['aria-invalid'] = true;
    if (required && p['aria-required'] == null) extra['aria-required'] = true;
    if (isNativeControl(target)) {
      if (!p.className && !NO_INPUT_CLASS.has(p.type)) extra.className = 'cw-input';
      if (error && p.style) extra.style = { ...p.style, borderColor: 'var(--cs-red-dark, #dc2626)' };
    }
    const cloned = React.cloneElement(target, extra);
    content = all.map((c, i) => (i === nativeIndex ? cloned : c));
  }

  const grouped = !target || onlyCustom;
  return (
    <div
      className={`cw-field${error ? ' has-error' : ''} ${className}`.trim()}
      style={style}
      role={grouped ? 'group' : undefined}
      aria-labelledby={grouped ? labelId : undefined}
    >
      {label != null && label !== '' && (
        <label id={labelId} htmlFor={target ? controlId : undefined} className="cw-label">
          {label}
          {required && <span className="cw-req" aria-hidden="true"> *</span>}
        </label>
      )}
      {content}
      {error && (
        <p id={errId} className="cw-field-error">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
        </p>
      )}
      {hint && <p id={hintId} className="cw-field-hint">{hint}</p>}
    </div>
  );
};

/**
 * Back-compat inline style for inputs. Prefer `className="cw-input"` (Field adds
 * it automatically). Values reference workspace tokens so they follow the brand
 * and switch to 16px on phones (no iOS zoom).
 */
export const inputStyle = {
  width: '100%',
  minHeight: 'var(--cw-input-h, 40px)',
  border: '1px solid var(--cw-input-border, #d1d5db)',
  borderRadius: 'var(--cs-radius, 8px)',
  padding: '0.5rem 0.75rem',
  fontSize: 'var(--cw-input-font, 0.875rem)',
  lineHeight: 1.4,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: 'var(--cs-white, #fff)',
  color: 'var(--cs-text-primary, #111827)',
};

export default Modal;
